/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react'
import {
  useState, useCallback, useReducer,
} from 'react'

import {
  Form, FormikProvider, useFormik, useFormikContext,
} from 'formik'
import { useMutation } from 'react-relay'
import PropTypes from 'prop-types'
import { getLocalStorageValue, useLocalStorage } from '@pareto-engineering/utils/src'
import { useLocation } from 'react-router-dom'
import appConfig from 'config'
import { extractLeadAttributesFromValues, prepareSectionMapAsInitialEntries } from './common/helpers'
import { isNotEmpty } from './common/validators'
import Context from './Context'
import { useUTMParameters } from '../UTMTracker'

const errorMap = {
  ALREADY_COMPLETED_SURVEY:<p>
    It appears like you’ve already completed this survey.
    {' '}
    If you need support, get in touch directly by emailing us at
    {' '}
    <a href={`mailto:${appConfig.SITE.SUPPORT_EMAIL}`}>{appConfig.SITE.SUPPORT_EMAIL}</a>
    {/* eslint-disable-next-line */}
  </p>,
  UNKNOWN:<p>
    It seems like we are having trouble with this form. While we take a lot
    of care, these things happen sometimes. We would really appreciate if you can
    get in touch with us at
    {' '}
    <a href={`mailto:${appConfig.SITE.SUPPORT_EMAIL}`}>{appConfig.SITE.SUPPORT_EMAIL}</a>
    {' '}
    where we can finish things off there.
    {/* eslint-disable-next-line */}
  </p>,
}

const UPSERT_USER_SURVEY = graphql`
    mutation ConversionFormContextProviderUpsertUserSurveyMutation($input: UpsertUserSurveyMutationInput!) {
      upsertUserSurvey(input: $input) {
        email
      }
    }
  `

const conversionReducer = (state, action) => {
  switch (action.type) {
    case 'SET_SECTION_INDEX':
      return {
        ...state,
        currentSectionIndex:action.payload,
        currentSection     :state.allSections[action.payload],
        progressPercentage :state.allSections[action.payload].overrideProgressPercentage
          ? state.allSections[action.payload].overrideProgressPercentage
          : (100 / state.allSections.length) * (action.payload + 1),
        isFirstSection:action.payload === 0,
        isLastSection :action.payload === state.allSections.length - 1,
      }

    default:
      return state
  }
}

const useCanContinue = (currentSectionFieldNameArray) => {
  /*
  This is fired before the validators would run and lives within the scope
  of the navigation rather than formik validation.
  */
  const { values, errors } = useFormikContext()
  if (!values || !Object.keys(values)) return false

  return !Object.keys(values)
    .filter((fieldName) => currentSectionFieldNameArray.includes(fieldName))
    .map((fieldName) => values[fieldName] !== '' && !errors[fieldName])
    .includes(false)
}

// Helper function to return a list of required fields
const getRequiredFieldNamesFromSection = (visibleInputFields) => visibleInputFields
  .filter((item) => !item.isOptional)
  .map((item) => item.name)

const addIsNotEmptyValidatorsToInputs = (inputs) => inputs.map((item) => (!item.isOptional
  ? ({ validate: isNotEmpty, ...item })
  : item
))

export const useConversionFormHandler = (sectionInputConfig) => {
  const [commit, isInFlight] = useMutation(UPSERT_USER_SURVEY)
  const [state, dispatch] = useReducer(conversionReducer, {
    currentSectionIndex:0,
    currentSection     :sectionInputConfig[0],
    allSections        :sectionInputConfig,
    progressPercentage :sectionInputConfig[0].overrideProgressPercentage
          || 100 / sectionInputConfig.length,
    isFirstSection:true,
    isLastSection :sectionInputConfig.length > 1,
  })
  const { values, setStatus } = useFormikContext()

  const utmParams = useUTMParameters()

  const setNextSectionIndex = useCallback((sectionIndex) => {
    if (sectionIndex !== sectionInputConfig.length - 1) {
      dispatch({
        type   :'SET_SECTION_INDEX',
        payload:state.currentSectionIndex + sectionIndex,
      })
    }
  }, [state.currentSectionIndex])

  const setPrevSectionIndex = useCallback((sectionIndex = 1) => {
    if (sectionIndex >= 0) {
      dispatch({
        type   :'SET_SECTION_INDEX',
        payload:state.currentSectionIndex - sectionIndex,
      })
    }
  }, [state.currentSectionIndex])
  const { pathname } = useLocation()
  const postDataAndBumpSection = (variables) => {
    const additionalData = {
      preferredPlan     :getLocalStorageValue('plan', ''),
      // We don't want to count the last step (nothing is saved it's just an information page).
      progressPercentage:(100 / (state.allSections.length - 1)) * (state.currentSectionIndex + 1),
    }
    const {
      fullName, ...usableVariables
    } = variables
    commit({
      variables:{
        input:{
          ...utmParams,
          ...usableVariables,
          pagePath       :pathname,
          extraSurveyData:JSON.stringify(additionalData),
          leadPriorities :JSON.stringify(usableVariables.leadPriorities),
        },
      },
      onCompleted(data) {
        const { email: leadEmail } = data.upsertUserSurvey
        setStatus(undefined)

        // Go to next slide only if the userLead has been created
        if (leadEmail) { setNextSectionIndex(1) }
      },
      onError(error) {
        const errorMessage = errorMap[error?.res?.errors[0].message]
          || errorMap.UNKNOWN
        setStatus(errorMessage)
      },
    })
  }

  const checkFieldConditionAndReturnVisible = (initialMap) => initialMap?.filter((item) => {
    if (!values) return item
    if (!item.condition) return item
    if (item.condition(values) === true) return item
    return null
  })
  const visibleInputFields = checkFieldConditionAndReturnVisible(
    addIsNotEmptyValidatorsToInputs(state.currentSection.inputs || []),
  ) || []
  const canProceed = useCanContinue(getRequiredFieldNamesFromSection(visibleInputFields))

  const prepareValuesForSubmission = () => {
    const surveyComplete = sectionInputConfig
      .findLastIndex((section) => section.save === true) === state.currentSectionIndex

    // Excluded variables that don't map to a particular field
    const { fullName, ...mappedValues } = values

    const {
      email, company: companyName,
    } = mappedValues
    const { firstName, lastName, company } = extractLeadAttributesFromValues(
      fullName,
      email,
      companyName,
    )
    // Since we initialise all values in Formik, let's exclude the ones that have
    // blank (i.e unanswered) values
    const valuesWithoutBlank = Object
      .entries(mappedValues)
      .reduce((acc, [k, v]) => (v !== '' ? { ...acc, [k]: v } : acc), {})

    return {
      ...valuesWithoutBlank, firstName, lastName, company, surveyComplete,
    }
  }
  const upsertConversionForm = () => postDataAndBumpSection(prepareValuesForSubmission())
  const dispatchers = {
    setNextSectionIndex,
    setPrevSectionIndex,
  }
  const { Content } = state.currentSection

  const submissionState = {
    canProceed,
    isInFlight,
  }
  return {
    ...state,
    ...dispatchers,
    ...submissionState,
    sectionMap      :state.allSections,
    visibleInputFields,
    Content,
    commit          :upsertConversionForm,
    setPreferredPlan:(value) => useLocalStorage('plan', value),
  }
}

const ConversionFormContextProvider = ({
  sectionMap,
  children,
  // ...otherProps
}) => {
  const conversionForm = useConversionFormHandler(sectionMap)
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Context.Provider value={{
      ...conversionForm,
      sectionMap,
      isOpen,
      setIsOpen,
    }}
    >
      {children}
    </Context.Provider>
  )
}

const ContextProviderWrapper = ({
  sectionMap,
  children,
}) => {
  const initialValues = prepareSectionMapAsInitialEntries(sectionMap)
  const formik = useFormik({
    initialValues,
    onSubmit       :() => null,
    validateOnMount:true,
  })

  return (
    <FormikProvider value={formik}>
      <Form>
        <ConversionFormContextProvider sectionMap={sectionMap}>
          {children}
        </ConversionFormContextProvider>
      </Form>
    </FormikProvider>
  )
}

ContextProviderWrapper.propTypes = {
  /**
   * The section configuration
   */
  sectionMap:PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)).isRequired,

  /**
   *  The children JSX
   */
  children:PropTypes.node,
}

ContextProviderWrapper.defaultProps = {
  // someProp:false
}

export default ContextProviderWrapper
